import React, { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { useParams } from 'react-router-dom';
import { Carousel, CarouselItem } from '@vodacomsa/ns-react-components';
import styles from './ApplyProduct.module.css';
import { Colors, Fonts } from '../../../constants';
import { useIsSuperMobile, productListFetch, userFirstName, allBusinessLenderStatusesSelector, postLoginBusinessesSelector, btaAccountSummary } from '../../../recoil';
import { ProductContainer } from '../../../components';
import { getCtaConfig } from '../../../helpers/productConfig';

export const ApplyProduct = () => {

  const isSuperMobile = useRecoilValue(useIsSuperMobile);
  const productList = useRecoilValue(productListFetch({}));
  const userName = useRecoilValue(userFirstName);
  const allBusinessLenderStatuses = useRecoilValue(allBusinessLenderStatusesSelector);
  const businesses = useRecoilValue(postLoginBusinessesSelector);
  const { businessId } = useParams();

  const businessName = useMemo(
    () => businesses.find(b => b.businessId === businessId)?.businessName ?? null,
    [businesses, businessId]
  );

  const btaApplicationId = allBusinessLenderStatuses?.[businessId]?.['LLL#BTA']?.applicationId ?? null;
  const btaAccountData = useRecoilValue(btaAccountSummary(btaApplicationId));

  const stylesRef = {
    main: {
      textAlign: 'center'
    },
    productContainer: {
      display: 'flex',
      flexFlow: 'wrap',
      backgroundColor: Colors.GreyScale1,
      color: Colors.GreyScale13,
      justifyContent: 'flex-start'
    },
    h1: {
      fontFamily: Fonts.Roboto,
      fontWeight: '500',
      fontSize: '30px',
      lineHeight: '36px',
      letterSpacing: '-3%',
      textAlign: 'center',
      margin: '40px 20px 30px'
    }
  };

  const restrictedKeys = useMemo(() => {
    if (!businessId) {
      return [];
    }

    const statuses = allBusinessLenderStatuses?.[businessId];

    if (!statuses) {
      return [];
    }

    const metaKeys = new Set(['hasQuoteAvailable', 'inProgressProductKeys']);
    const activeKey = Object.entries(statuses)
      .filter(([key]) => !metaKeys.has(key))
      .find(([, val]) => val?.status === 'ACTIVE')?.[0] ?? null;

    const inProgressKeys = statuses.inProgressProductKeys ?? [];
    return [...new Set([activeKey, ...inProgressKeys].filter(Boolean))];
  }, [allBusinessLenderStatuses, businessId]);

  const visibleProducts = useMemo(() => {
    const all = productList?.nonMatchingProducts ?? [];
    const filtered = restrictedKeys.length
      ? all.filter(p => p.partnerCode !== 'LLL' || restrictedKeys.includes(`${p.partnerCode}#${p.productCode}`))
      : all;
    return [...filtered].sort((a, b) => {
      const aRec = restrictedKeys.includes(`${a.partnerCode}#${a.productCode}`) ? 0 : 1;
      const bRec = restrictedKeys.includes(`${b.partnerCode}#${b.productCode}`) ? 0 : 1;
      return aRec - bRec;
    });
  }, [productList, restrictedKeys]);

  const getRecommendedCta = (product) => {
    const productKey = `${product.partnerCode}#${product.productCode}`;
    const lenderEntry = allBusinessLenderStatuses?.[businessId]?.[productKey];
    const lenderStatus = lenderEntry?.status ?? 'NONE';
    const isReadvanceEligible = productKey === 'LLL#BTA'
      ? btaAccountData?.isReadvanceEligible
      : lenderEntry?.isReadvanceEligible;
    const ctaConfig = getCtaConfig(productKey, lenderStatus, {
      inProgressProductKeys: allBusinessLenderStatuses?.[businessId]?.inProgressProductKeys ?? [],
      isReadvanceEligible
    });
    return {
      ctaLabel: ctaConfig?.label ?? null,
      ctaAction: ctaConfig?.action ?? null,
      ctaDisabled: ctaConfig?.disabled ?? false,
      applicationId: lenderEntry?.applicationId ?? null
    };
  };

  const renderProducts = (products) => {

    if (!products?.length) {
      return null;
    }

    return isSuperMobile ? (
      <div className={styles.carouselContainer}>
        <Carousel dots className={styles.carouselCustom}>
          {products.map((product) => {
            const productKey = `${product.partnerCode}#${product.productCode}`;
            const isRecommended = restrictedKeys.includes(productKey);
            const { ctaLabel, ctaAction, ctaDisabled, applicationId } = isRecommended ? getRecommendedCta(product) : {};
            return (
              <CarouselItem key={product.productId} className={styles.carouselItem}>
                <ProductContainer
                  showProductCriteria={false}
                  showApplyButton={true}
                  isRecommended={isRecommended}
                  ctaLabel={ctaLabel}
                  ctaAction={ctaAction}
                  ctaDisabled={ctaDisabled}
                  applicationId={applicationId}
                  businessName={businessName}
                  {...product}
                />
              </CarouselItem>
            );
          })}
        </Carousel>
      </div>
    ) : (
      <div style={stylesRef.productContainer}>
        {products.map((product) => {
          const productKey = `${product.partnerCode}#${product.productCode}`;
          const isRecommended = restrictedKeys.includes(productKey);
          const { ctaLabel, ctaAction, ctaDisabled, applicationId } = isRecommended ? getRecommendedCta(product) : {};
          return (
            <ProductContainer
              key={product.productId}
              showProductCriteria={false}
              showApplyButton={true}
              isRecommended={isRecommended}
              ctaLabel={ctaLabel}
              ctaAction={ctaAction}
              ctaDisabled={ctaDisabled}
              applicationId={applicationId}
              businessName={businessName}
              {...product}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div style={stylesRef.main}>
      <h2 className={styles.usernameContainer}>{userName ? `Hi ${userName}` : ''}</h2>
      <h1 className={styles.pageHeader}>{'Your active loans, renewal options, and new business funding solutions.'}</h1>
      <p className={styles.pageContent}>Whether you’re growing your team, investing in equipment, or launching your next big project, our flexible business lending solutions are designed to back you. Get the funding you need on terms that fit your pace and ambition.</p>
      {renderProducts(visibleProducts)}
    </div>
  );
};